import { useDocMetaHelper } from '@affine/core/components/hooks/use-block-suite-page-meta';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { getWorkspaceDocsWithOwnersQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { PageIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SettingsEntityAvatar,
  SettingsEntityIdentityAside,
  SettingsEntityList,
  SettingsEntityMessage,
  SettingsExpandableItem,
  SettingsMetaItem,
} from '../shared/settings-entity';
import { DocPermissionsEditor } from './doc-permissions-editor';
import * as styles from './styles.css';

interface DocOwnerInfo {
  id: string;
  name: string;
  avatarUrl: string | null;
}

interface DocWithOwner {
  id: string;
  title: string;
  updatedDate?: number;
  owner?: DocOwnerInfo;
}

interface DocListViewProps {
  searchQuery: string;
}

export const DocListView = ({ searchQuery }: DocListViewProps) => {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const docCollection = workspaceService.workspace.docCollection;
  const workspaceId = workspaceService.workspace.id;
  const { getDocMeta } = useDocMetaHelper();

  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
  const [docOwners, setDocOwners] = useState<Map<string, DocOwnerInfo>>(
    new Map()
  );

  // Load doc owners from server
  useEffect(() => {
    const loadOwners = async () => {
      if (!serverService.server) return;

      try {
        const result = await serverService.server.gql({
          query: getWorkspaceDocsWithOwnersQuery,
          variables: {
            workspaceId,
            pagination: { first: 500 },
          },
        });

        const owners = new Map<string, DocOwnerInfo>();
        const edges = result.workspace?.docs?.edges ?? [];
        for (const edge of edges) {
          if (edge.node.createdBy) {
            owners.set(edge.node.id, {
              id: edge.node.createdBy.id,
              name: edge.node.createdBy.name,
              avatarUrl: edge.node.createdBy.avatarUrl ?? null,
            });
          }
        }
        setDocOwners(owners);
      } catch (err) {
        console.debug('Failed to load doc owners:', err);
      }
    };

    void loadOwners();
  }, [serverService.server, workspaceId]);

  // Get all docs from docCollection with owner info
  const allDocs = useMemo(() => {
    const docs: DocWithOwner[] = [];
    docCollection.docs.forEach((_doc, id) => {
      const meta = getDocMeta(id);
      if (meta && !meta.trash) {
        docs.push({
          id,
          title: meta.title || 'Untitled',
          updatedDate: meta.updatedDate,
          owner: docOwners.get(id),
        });
      }
    });
    return docs.sort((a, b) => (b.updatedDate || 0) - (a.updatedDate || 0));
  }, [docCollection.docs, getDocMeta, docOwners]);

  // Filter docs by search query
  const filteredDocs = useMemo(() => {
    if (!searchQuery.trim()) return allDocs;
    const query = searchQuery.toLowerCase();
    return allDocs.filter(
      doc =>
        doc.title.toLowerCase().includes(query) ||
        doc.id.toLowerCase().includes(query) ||
        doc.owner?.name.toLowerCase().includes(query)
    );
  }, [allDocs, searchQuery]);

  const handleDocClick = useCallback((docId: string) => {
    setExpandedDocId(prev => (prev === docId ? null : docId));
  }, []);

  if (filteredDocs.length === 0) {
    return (
      <SettingsEntityMessage>
        {searchQuery
          ? (t[
              'com.streamlain.settings.workspace.permissions.no-docs-found'
            ]?.() ?? 'No documents found matching your search.')
          : (t['com.streamlain.settings.workspace.permissions.no-docs']?.() ??
            'No documents in this workspace.')}
      </SettingsEntityMessage>
    );
  }

  return (
    <SettingsEntityList>
      {filteredDocs.map(doc => (
        <DocListItem
          key={doc.id}
          doc={doc}
          isExpanded={expandedDocId === doc.id}
          onClick={() => handleDocClick(doc.id)}
        />
      ))}
    </SettingsEntityList>
  );
};

interface DocListItemProps {
  doc: DocWithOwner;
  isExpanded: boolean;
  onClick: () => void;
}

const DocListItem = ({ doc, isExpanded, onClick }: DocListItemProps) => {
  const t = useI18n();

  const formattedDate = useMemo(() => {
    if (!doc.updatedDate) return '';
    const date = new Date(doc.updatedDate);
    return date.toLocaleDateString();
  }, [doc.updatedDate]);

  return (
    <SettingsExpandableItem
      expanded={isExpanded}
      onToggle={onClick}
      leading={<PageIcon className={styles.listItemIcon} />}
      title={doc.title || t['Untitled']?.() || 'Untitled'}
      meta={
        formattedDate ? (
          <SettingsMetaItem>{`Updated ${formattedDate}`}</SettingsMetaItem>
        ) : undefined
      }
      aside={
        doc.owner ? (
          <SettingsEntityIdentityAside
            avatar={
              <SettingsEntityAvatar
                size={20}
                userId={doc.owner.id}
                name={doc.owner.name}
                url={doc.owner.avatarUrl}
              />
            }
            name={doc.owner.name}
          />
        ) : undefined
      }
    >
      <DocPermissionsEditor docId={doc.id} />
    </SettingsExpandableItem>
  );
};
